import { useEffect, useState } from 'preact/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Banner, Button, Card } from '../components/ui'
import { getKnowledge, getMe, saveKnowledge } from '../api/endpoints'
import { siteMeta } from '../utils/site'
import type { SiteId } from '../types'

/**
 * Per-site knowledge editor. The bot reads this on every chat (with the
 * legacy KV value as a fallback for sites never edited in the PWA).
 *
 * No structure imposed — operators paste any markdown. They typically:
 *   - Run npm run build-knowledge locally to get an auto-scraped summary,
 *     then paste it here as a starting point
 *   - Add labelled "## Reference doc: ..." sections with extracts from
 *     PDFs / brochures / pricing sheets they want the bot to know about
 *   - Edit auto-scraped facts the bot got wrong
 */
export function KnowledgePage() {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe })
  const sites: SiteId[] = meQuery.data?.sites ?? []
  const [selectedSite, setSelectedSite] = useState<SiteId | null>(null)

  useEffect(() => {
    if (!selectedSite && sites.length > 0) setSelectedSite(sites[0])
  }, [sites, selectedSite])

  if (meQuery.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <span class="spinner spinner-dark" />
      </div>
    )
  }
  if (sites.length === 0) {
    return (
      <Banner variant="warn">
        No sites assigned to your account — ask an admin to grant access.
      </Banner>
    )
  }
  if (!selectedSite) return null

  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <h1 style={{ marginBottom: 6 }}>Knowledge</h1>
        <p
          style={{
            color: 'var(--m-text-muted)',
            fontSize: 'var(--m-text-sm)',
          }}
        >
          The factual base the bot answers from. Paste any markdown — auto-
          scraped website summaries, PDF excerpts, pricing tables, FAQs,
          policies. Saved instantly — the next chat picks it up.
        </p>
      </div>

      {sites.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <select
            class="input"
            value={selectedSite}
            onChange={(e) =>
              setSelectedSite(
                (e.currentTarget as HTMLSelectElement).value as SiteId
              )
            }
            style={{ maxWidth: 320 }}
          >
            {sites.map((s) => (
              <option key={s} value={s}>
                {siteMeta(s).name}
              </option>
            ))}
          </select>
        </div>
      )}

      <KnowledgeForm site={selectedSite} />
    </>
  )
}

function KnowledgeForm({ site }: { site: SiteId }) {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['knowledge', site],
    queryFn: () => getKnowledge(site),
  })

  const [draft, setDraft] = useState('')
  const [savedAt, setSavedAt] = useState<number | null>(null)

  // Hydrate draft when data loads or site changes
  useEffect(() => {
    if (data) {
      setDraft(data.knowledgeText)
      setSavedAt(null)
    }
  }, [data, site])

  const save = useMutation({
    mutationFn: () => saveKnowledge(site, draft),
    onSuccess: (res) => {
      setDraft(res.knowledgeText)
      setSavedAt(Date.now())
      queryClient.invalidateQueries({ queryKey: ['knowledge', site] })
    },
  })

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <span class="spinner spinner-dark" />
      </div>
    )
  }
  if (isError) {
    return (
      <Banner variant="error">
        Couldn’t load knowledge. <a onClick={() => refetch()}>Retry</a>
      </Banner>
    )
  }

  const dirty = draft !== (data?.knowledgeText ?? '')
  const charCount = draft.length

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (dirty) save.mutate()
      }}
    >
      {data?.source === 'legacy_kv' && (
        <Banner variant="info" style={{ marginBottom: 12 }}>
          Showing knowledge from the legacy build-knowledge.ts upload. Saving
          here moves it into the new editable store — no functional change,
          just sets the operator-edit pathway as authoritative for this site.
        </Banner>
      )}
      {data?.source === 'empty' && (
        <Banner variant="warn" style={{ marginBottom: 12 }}>
          No knowledge yet for this site. The bot has only the playbook + a
          generic placeholder to work with. Paste a starting summary below
          (run <code>npm run build-knowledge</code> locally for an auto-
          scraped first draft, or write one manually).
        </Banner>
      )}

      <Card>
        <textarea
          class="textarea"
          rows={24}
          placeholder="# Business name&#10;&#10;## Company overview&#10;...&#10;&#10;## Services&#10;...&#10;&#10;## Reference doc: 2026 pricing brochure&#10;..."
          value={draft}
          onInput={(e) =>
            setDraft((e.currentTarget as HTMLTextAreaElement).value)
          }
          maxLength={60_000}
          style={{ fontFamily: 'ui-monospace, SF Mono, monospace', fontSize: 13 }}
        />
        <div
          style={{
            marginTop: 6,
            fontSize: 'var(--m-text-xs)',
            color: 'var(--m-text-muted)',
            textAlign: 'right',
          }}
        >
          {charCount.toLocaleString()} / 60,000 chars
          {charCount > 50_000 && ' — close to limit; consider RAG for product catalogues'}
        </div>
      </Card>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--m-surface, #fff)',
          padding: '12px 0',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
        }}
      >
        <Button
          type="submit"
          variant="primary"
          loading={save.isPending}
          disabled={!dirty || save.isPending}
        >
          Save knowledge
        </Button>
        {savedAt && !dirty && (
          <span
            style={{
              color: 'var(--m-text-muted)',
              fontSize: 'var(--m-text-sm)',
            }}
          >
            Saved · live in the bot now
          </span>
        )}
        {save.isError && (
          <span
            style={{
              color: 'var(--m-danger, #c33)',
              fontSize: 'var(--m-text-sm)',
            }}
          >
            Save failed — try again
          </span>
        )}
      </div>
    </form>
  )
}
